import Modal from "@via/components/modal";
import { ModalSize } from "@via/utils/constants";
import { useEffect, useMemo, useState } from "react";
import { useUpdateVmetaData } from "@via/pages/projects/metadata/metadata.api.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { NOTIFICATION } from "@via/context/notifications-context";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import { VmetaDataResponse, VmetaPermissionV2 } from "common";
import { useFieldArray, useForm } from "react-hook-form";
import FormInputComponent from "@via/components/inputs/form-input-component";
import {
  addUserGroupMemberOptions,
  requiredValidation,
} from "@via/utils/form-validators";
import SelectComponent from "@via/components/select/select";
import {
  PREMIUM_DATASET_PERMISSION_OPTIONS,
  USER_ROLES,
} from "@via/utils/constants";
import { useAuth } from "@via/hooks/useAuth";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";
import {
  deduplicatePermissions,
  filterPermissions,
  transformPermissions,
} from "@via/pages/data/premium-dataset/utils/helper";
import { Spinner } from "@via/components/loading/loading";

interface EditCollectionPermissionProps {
  collectionFiles: VmetaDataResponse[];
  canvasId: string;
  collectionName: string;
  isLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const everyoneOption = { value: "everyone", label: "Everyone" };

const defaultPermission = {
  type: "user",
  email: "",
  read: true,
  write: false,
};

const EditCollectionPermission = ({
  collectionFiles,
  canvasId,
  collectionName,
  isLoading,
  isOpen,
  onClose,
}: EditCollectionPermissionProps) => {
  const { user } = useAuth();
  const { setNotification } = useNotifications();
  const [errors, setErrors] = useState<string[]>([]);

  const { mutateAsync: updateVmetaData } = useUpdateVmetaData();

  // Get permissions from the first row
  const firstRowPermissions = useMemo(() => {
    if (!collectionFiles || collectionFiles.length === 0) return [];
    const perms = (collectionFiles[0]?.perms as VmetaPermissionV2) || [];
    // Transform V2 format to form format
    return perms.map(perm => {
      if (perm.type === "user") {
        return {
          type: "user",
          email: perm.email || perm.username || "",
          read: perm.read || false,
          write: perm.write || false,
        };
      } else if (perm.type === "group") {
        return {
          type: "group",
          groupId: perm.groupId || "",
          read: perm.read || false,
          write: perm.write || false,
        };
      } else {
        return {
          type: "everyone",
          read: perm.read || false,
          write: perm.write || false,
        };
      }
    });
  }, [collectionFiles]);

  const missingPermIds = useMemo(() => {
    return (
      (collectionFiles[0]?.perms as VmetaPermissionV2)
        ?.filter(perm => perm.type === "group")
        .map(perm => perm.groupId) || []
    );
  }, [collectionFiles]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors: formErrors, isSubmitting },
    reset,
  } = useForm<{
    perms: any[];
  }>({
    mode: "onSubmit",
    defaultValues: {
      perms: firstRowPermissions,
    },
  });

  // Reset form when permissions change
  useEffect(() => {
    reset({ perms: firstRowPermissions });
  }, [firstRowPermissions, reset]);

  const permissionOptions = useMemo(() => {
    if (user && user?.userRole === USER_ROLES.ADMIN) {
      return [everyoneOption, ...PREMIUM_DATASET_PERMISSION_OPTIONS];
    } else {
      return PREMIUM_DATASET_PERMISSION_OPTIONS;
    }
  }, [user]);

  const {
    fields,
    append: appendPerms,
    remove: removePerms,
  } = useFieldArray({
    control,
    name: "perms",
  });

  const handleUpdatePermissions = async (payload: { perms: any[] }) => {
    try {
      const transformedPerms = transformPermissions(payload.perms);
      const filteredPerms = filterPermissions(transformedPerms);
      const uniquePerms = deduplicatePermissions(filteredPerms);

      const results = await Promise.allSettled(
        collectionFiles.map(async file => {
          const fileId = file._id?.toString() || "";
          return await updateVmetaData({
            canvasId,
            collectionName,
            id: fileId,
            perms: uniquePerms,
          });
        })
      );

      const errorRowIndices: number[] = [];
      const rowErrors: string[] = [];

      results.forEach((result, index) => {
        if (result.status === "rejected") {
          errorRowIndices.push(index);
          rowErrors.push(
            result.reason?.response?.data?.message ||
              "Failed to update permissions"
          );
        } else if (result.status === "fulfilled" && "error" in result.value) {
          errorRowIndices.push(index);
          rowErrors.push(
            (result?.value?.error as string) || "Failed to update permissions"
          );
        }
      });

      if (rowErrors.length > 0) {
        setErrors(rowErrors);
      } else {
        setErrors([]);
        setNotification({
          id: "permissions-updated",
          message: "Permissions updated successfully",
          variant: NOTIFICATION.SUCCESS,
        });
        onClose();
      }
    } catch (error) {
      setNotification({
        id: "error-updating-permissions",
        message: "Error updating permissions",
        details: error instanceof Error ? error.message : "Unknown error",
        variant: NOTIFICATION.ERROR,
      });
    }
  };

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      title="Edit Collection Permissions"
      showButtons={false}
      isLoading={isLoading}
      size={ModalSize.Large}>
      <>
        <MessageBanner messages={errors} type={BannerType.Error} />
        <MessageBanner
          messages={
            [
              "These permission changes will be applied to all selected rows. All selected rows will have the same permissions after saving.",
            ] as string[]
          }
          type={BannerType.Warning}
        />
        <form
          onSubmit={e => {
            e.preventDefault();
            void handleSubmit(handleUpdatePermissions)(e);
          }}>
          <div className="space-y-6 mt-2">
            <div className="flex justify-between items-center">
              <label className="flex items-center text-sm font-medium leading-6 text-gray-900">
                Permissions
              </label>
              <button
                type="button"
                onClick={() => appendPerms(defaultPermission)}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                + Add Permission
              </button>
            </div>
            <div className="flex flex-col px-2 sm:px-4 max-h-[500px] overflow-auto">
              {fields.map((field, idx) => {
                const type = watch(`perms.${idx}.type`);
                const isEveryone = type === "everyone";
                return (
                  <div
                    key={field.id}
                    className="flex items-center gap-2 border-b py-2">
                    <div className="min-w-[150px]">
                      <SelectComponent
                        control={control}
                        name={`perms.${idx}.type`}
                        options={permissionOptions}
                        placeholder="Select type"
                        enableClear={false}
                      />
                    </div>
                    {type === "user" && (
                      <div className="-mt-2 w-full">
                        <FormInputComponent
                          control={control}
                          name={`perms.${idx}.email`}
                          placeholder="Enter email"
                          validations={addUserGroupMemberOptions.email}
                        />
                      </div>
                    )}
                    {type === "group" && (
                      <div className="w-full">
                        <UserGroupSelectComponent
                          control={control}
                          name={`perms.${idx}.groupId`}
                          rules={requiredValidation("User Group")}
                          menuPlacement="top"
                          errors={formErrors}
                          uniqueIdentifier="vmetaGroupId"
                          missingId={missingPermIds}
                        />
                      </div>
                    )}
                    <label className="flex items-center gap-1">
                      <FormInputComponent
                        control={control}
                        name={`perms.${idx}.read`}
                        type="checkbox"
                        className="h-6 w-6 -mt-2"
                      />
                      Read
                    </label>
                    {!isEveryone && (
                      <label className="flex items-center gap-1">
                        <FormInputComponent
                          control={control}
                          name={`perms.${idx}.write`}
                          type="checkbox"
                          className="h-6 w-6 mr-2 -mt-2"
                        />
                        Write
                      </label>
                    )}
                    <button
                      type="button"
                      onClick={() => removePerms(idx)}
                      className="px-2 text-gray-500 hover:text-gray-700">
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end mt-6 gap-3">
            <button
              type="button"
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
              <Spinner loading={isSubmitting}>Update</Spinner>
            </button>
          </div>
        </form>
      </>
    </Modal>
  );
};

export default EditCollectionPermission;
